import type { AxiosError } from 'axios';
import type { ApiErrorResponse } from './types';

/**
 * Pure builders for the breadcrumb payloads the auth-sensitive interceptors
 * emit to Sentry. Extracted so we can unit-test the shape of the data we
 * capture without mocking axios end-to-end.
 *
 * These functions are deliberately side-effect-free — the interceptor calls
 * them, then passes the returned objects to Sentry.addBreadcrumb. That keeps
 * interceptor logic thin and the critical diagnostic shape asserted in tests.
 */

export interface AuthRequestBreadcrumbInput {
  url: string;
  token: string | null;
  authHeaderAttached: boolean;
  skipAuth: boolean;
  flowId: string | null;
}

export interface AuthRequestBreadcrumbData {
  flowId: string | null;
  skipAuth: boolean;
  tokenPresentInStorage: boolean;
  authHeaderAttached: boolean;
  /** Count of dot-separated segments — 3 for a well-formed JWT. */
  jwtSegments: number;
  /** Total characters including dots — detects truncation. */
  jwtTotalLen: number;
  /** Length of the signature segment — 684 chars for RS256/RSA 4096. */
  jwtSigLen: number;
  /** Length of the payload segment — detects unexpectedly short/long payloads. */
  jwtPayloadLen: number;
}

/**
 * Build the `auth-request` breadcrumb payload. Captures JWT structural
 * metadata (segment count and lengths) but NEVER the token content, header,
 * or payload bytes.
 */
export function buildAuthRequestBreadcrumbData(
  input: AuthRequestBreadcrumbInput,
): AuthRequestBreadcrumbData {
  const parts = input.token ? input.token.split('.') : [];
  return {
    flowId: input.flowId,
    skipAuth: input.skipAuth,
    tokenPresentInStorage: !!input.token,
    authHeaderAttached: input.authHeaderAttached,
    jwtSegments: parts.length,
    jwtTotalLen: input.token?.length ?? 0,
    jwtSigLen: parts[2]?.length ?? 0,
    jwtPayloadLen: parts[1]?.length ?? 0,
  };
}

export interface Auth401BreadcrumbInput {
  flowId: string | null;
  hadToken: boolean;
  hadAuthHeader: boolean;
  optionalAuth: boolean;
  error: AxiosError<ApiErrorResponse>;
}

export interface Auth401BreadcrumbData {
  flowId: string | null;
  tokenPresentBeforeClear: boolean;
  authHeaderWasAttached: boolean;
  optionalAuth: boolean;
  /** Body message from the server (e.g. "Invalid JWT Token"). */
  serverMessage: string | undefined;
  /** Numeric code from the server body, if present. */
  serverCode: number | undefined;
  /** Lexik/backend correlation id (X-Auth-Debug-Id header). */
  debugId: string | undefined;
  /** Response body size — distinguishes Lexik 401 variants (42 vs 44 bytes). */
  responseBytes: number;
}

/**
 * Extract the response data, headers, and debug metadata for the 401
 * breadcrumb. Does not touch Sentry. Returns undefined fields when the
 * upstream data is missing so the interceptor can pass the object straight
 * to Sentry.addBreadcrumb.
 */
export function buildAuth401BreadcrumbData(
  input: Auth401BreadcrumbInput,
): Auth401BreadcrumbData {
  const { error } = input;
  const data = error.response?.data as ApiErrorResponse | undefined;
  const headers = error.response?.headers as Record<string, string> | undefined;
  const debugId = typeof headers?.['x-auth-debug-id'] === 'string'
    ? headers['x-auth-debug-id']
    : undefined;
  const serverMessage = typeof data?.message === 'string' ? data.message : undefined;
  const serverCode = typeof data?.code === 'number' ? data.code : undefined;
  const responseBytes = data ? JSON.stringify(data).length : 0;

  return {
    flowId: input.flowId,
    tokenPresentBeforeClear: input.hadToken,
    authHeaderWasAttached: input.hadAuthHeader,
    optionalAuth: input.optionalAuth,
    serverMessage,
    serverCode,
    debugId,
    responseBytes,
  };
}
